/**
 * #1389 — the explicit jump to a game's Injuries disclosure on the Game
 * Detail page. A prop status (or the props section's report age) asks; the
 * game's `GameInjuries` in the panel answers by opening, scrolling itself into
 * view and focusing its toggle. A request made before that panel has mounted
 * waits here and is answered on mount. Board entries do not use this: they
 * open the board's own inline report and never touch the panel.
 *
 * The origin control is kept outside the store state (a DOM node is not
 * state): Escape after the jump returns focus to it, closing nothing.
 */
import { create } from 'zustand';
import { coreGameStateKey } from '@/lib/gameState/admission';
import { baseGameKey } from '@/services/analyticsGameKey';

export interface InjuryDisclosureRequest {
  /** Core's spelling of the game key, as `GameInjuries` keys itself. */
  gameKey: string;
  /** Wire playerKey of the targeted player row, if any. */
  playerKey: string | null;
  /** Team section to target when no player row is (a pending team). */
  team: string | null;
  seq: number;
}

export const useInjuryDisclosureStore = create<{ request: InjuryDisclosureRequest | null }>(() => ({ request: null }));

let seq = 0;
let origin: HTMLElement | null = null;

export function requestInjuryDisclosure(
  gameKey: string,
  opts: { playerKey?: string | null; team?: string | null; origin?: HTMLElement | null } = {},
): void {
  const key = coreGameStateKey(baseGameKey(gameKey));
  if (!key) return;
  origin = opts.origin ?? null;
  useInjuryDisclosureStore.setState({
    request: { gameKey: key, playerKey: opts.playerKey ?? null, team: opts.team ?? null, seq: ++seq },
  });
}

/** The control that made the last request, handed over once. */
export function takeInjuryDisclosureOrigin(): HTMLElement | null {
  const el = origin;
  origin = null;
  return el;
}

export function __resetInjuryDisclosureForTests(): void {
  origin = null;
  useInjuryDisclosureStore.setState({ request: null });
}
